import { useEffect, useRef, useState } from "react";

type Props = {
  // Accessible name for the file input (e.g. "Driver's license — front").
  label: string;
  // Short visible caption shown above the tile (e.g. "Front").
  caption: string;
  accept: string;
  uploaded: boolean;
  uploading: boolean;
  error?: string | undefined;
  onSelect: (file: File) => void;
  onRemove?: (() => void) | undefined;
};

// Build a preview object URL for image files only. PDFs (and environments
// without createObjectURL, e.g. jsdom) fall back to the file-icon state.
function makePreview(file: File): string | null {
  if (!file.type.startsWith("image/")) return null;
  try {
    return URL.createObjectURL(file) || null;
  } catch {
    return null;
  }
}

export function PhotoTile({
  label,
  caption,
  accept,
  uploaded,
  uploading,
  error,
  onSelect,
  onRemove,
}: Props) {
  // chooseRef opens the OS file picker (also reachable by clicking the tile,
  // which is a <label> for this input). cameraRef carries capture so mobile
  // launches the rear camera directly; desktop ignores it, so the button is
  // only shown on small screens.
  const chooseRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const inputId = `photo-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`;
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const allowsImages = accept.includes("image/");

  // Revoke the previous object URL when it changes and on unmount.
  useEffect(() => {
    if (!previewUrl) return;
    return () => {
      try { URL.revokeObjectURL(previewUrl); } catch { /* ignore */ }
    };
  }, [previewUrl]);

  function handleFiles(list: FileList | null) {
    const file = list?.[0];
    if (!file) return;
    setPreviewUrl(makePreview(file));
    onSelect(file);
  }

  const filled = uploaded || uploading;
  const tileClasses =
    "group relative flex aspect-[3/2] cursor-pointer items-center justify-center " +
    "overflow-hidden rounded-lg border text-center transition-colors " +
    (error
      ? "border-red-400 bg-red-50"
      : filled
        ? "border-slate-300 bg-slate-100"
        : "border-2 border-dashed border-slate-300 bg-slate-50 hover:border-blue-400 hover:bg-blue-50");

  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-slate-600">{caption}</span>
      <label htmlFor={inputId} className={tileClasses}>
        {previewUrl ? (
          <img
            src={previewUrl}
            alt={label}
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : uploaded ? (
          <FileIcon />
        ) : uploading ? null : (
          <span className="flex flex-col items-center gap-1 text-slate-400">
            <PlusIcon />
            <span className="text-xs font-medium">Add photo</span>
          </span>
        )}

        {uploading ? (
          <span className="absolute inset-0 flex items-center justify-center bg-white/70 text-xs font-medium text-slate-600">
            Uploading…
          </span>
        ) : null}

        {uploaded && !uploading ? (
          <span
            aria-hidden="true"
            className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-emerald-600 text-white shadow"
          >
            <CheckIcon />
          </span>
        ) : null}

        <input
          id={inputId}
          ref={chooseRef}
          type="file"
          accept={accept}
          className="sr-only"
          aria-label={label}
          onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
        />
      </label>

      <div className="flex items-center gap-3 text-xs font-medium">
        {allowsImages ? (
          <button
            type="button"
            onClick={() => cameraRef.current?.click()}
            className="text-blue-600 hover:text-blue-700 hover:underline sm:hidden"
          >
            Take photo
          </button>
        ) : null}
        {uploaded ? (
          <>
            <button
              type="button"
              onClick={() => chooseRef.current?.click()}
              className="text-blue-600 hover:text-blue-700 hover:underline"
            >
              Replace
            </button>
            {onRemove ? (
              <button
                type="button"
                onClick={() => { setPreviewUrl(null); onRemove(); }}
                className="text-red-600 hover:text-red-700 hover:underline"
              >
                Remove
              </button>
            ) : null}
          </>
        ) : null}
      </div>

      {allowsImages ? (
        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="sr-only"
          // The "Take photo" button is the user-visible control; this hidden
          // input is just the dialog it triggers. aria-hidden so screen
          // readers don't double-announce and label queries resolve to the
          // primary input above.
          aria-hidden="true"
          tabIndex={-1}
          onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
        />
      ) : null}

      {error ? <p className="text-xs text-red-600">{error}</p> : null}
    </div>
  );
}

function PlusIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" className="h-6 w-6"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" className="h-3.5 w-3.5"
      stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

function FileIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" className="h-8 w-8 text-slate-400"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
    </svg>
  );
}
